/**
 * Desktop app: links to Bundle's own web app open in the app, not in the system browser.
 *
 * A link in a message (or anywhere on the page) to `https://app.bundle.direct/...` (or the old workers.dev host, or
 * the build's `webUrl`) whose path is one the app routes is opened here:
 * - a meeting (`/m/<code>`) or a published page (`/s/<token>`): its own Bundle window (`open_window`);
 * - a workspace URL (`/acme/c/general`, a thread, a page…): in this window's shell (the `bundle:open-url` event the
 *   Notifier routes to `shell.open`), or a new window when this window has no workspace shell (e.g. a meeting window).
 * Every other link keeps going to the system browser (the opener plugin's click handler, INTEGRATION.md §5).
 * Linux has no WebRTC in the app, so a meeting link goes to the browser there.
 *
 * Clicks on links are taken over, and links code opens with packages/ui's `openLink` (its cancelable
 * `bundle:open-link` event: the rich-text editor's ⌘-click, the share dialog's open button). Code that calls `window.open(webUrl)` on purpose
 * (sign-in, "Manage in browser", "Open huddle in browser") still reaches the browser.
 */
import { Huddles, Sharing, Shell } from "@bundle/core";
import { callsInBrowser, desktopInfo, isDesktop, openInBrowser, openInNewWindow } from "./desktop.ts";

/** Web origins that are this app: production, the pre-domain workers.dev host, and the build's own. */
function ownOrigins(): string[] {
  const origins = ["https://app.bundle.direct", "https://bundle-web.bigsmall.workers.dev"];
  const web = desktopInfo()?.webUrl;
  if (web !== undefined && web !== "") {
    try {
      origins.push(new URL(web).origin);
    } catch {
      // Not a URL: ignore it.
    }
  }
  return origins;
}

/** Single-segment paths that are pages of the web app, not workspace slugs. */
const RESERVED = new Set(["desktop-auth", "gallery", "invite", "m", "s", "signup"]);

export type AppRoute = { kind: "meeting" | "public" | "workspace"; path: string };

/** The in-app route an absolute URL points at, when it is one of this app's origins and a path the app routes. */
export function appRouteForUrl(href: string, origins: readonly string[] = ownOrigins()): AppRoute | undefined {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }
  // The page's own links (relative hrefs; in dev the web origin is the page's) are the page's business.
  if (url.origin === location.origin || !origins.includes(url.origin)) return undefined;
  const path = `${url.pathname}${url.search}${url.hash}`;
  if (Huddles.meetingCodeFromPath(url.pathname) !== undefined) return { kind: "meeting", path };
  if (Sharing.publicTokenFromPath(url.pathname) !== undefined) return { kind: "public", path };
  const route = Shell.parseUrl(path);
  if (route === undefined || (route.type === "home" && RESERVED.has(route.ws))) return undefined;
  return { kind: "workspace", path };
}

/** Opens an in-app route the way a link to it should open in the desktop app (see the module comment). */
export async function openAppRoute(route: AppRoute, href: string): Promise<void> {
  if (route.kind === "meeting" && callsInBrowser()) {
    await openInBrowser(href);
    return;
  }
  if (route.kind === "workspace") {
    // The Notifier's listener cancels the event when this window's workspace shell took the URL.
    const taken = !window.dispatchEvent(new CustomEvent("bundle:open-url", { detail: route.path, cancelable: true }));
    if (taken) return;
  }
  await openInNewWindow(route.path);
}

/**
 * Takes over clicks on links to this app (desktop only; a no-op in the browser). Registered on `document`, so it runs
 * before the opener plugin's `window` listener, which skips clicks that are already handled.
 */
export function installDesktopLinkHandler(): void {
  if (!isDesktop()) return;
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey) return;
    const a = e.composedPath().find((n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement);
    if (a === undefined || a.href === "") return;
    const route = appRouteForUrl(a.href);
    if (route === undefined) return;
    e.preventDefault();
    void openAppRoute(route, a.href).catch((err: unknown) => console.warn("failed to open", route.path, err));
  });
  // Links opened from code (@bundle/ui openLink): the same check as a click.
  window.addEventListener("bundle:open-link", (e) => {
    const href = (e as CustomEvent<string>).detail;
    const route = appRouteForUrl(href);
    if (route === undefined) return;
    e.preventDefault();
    void openAppRoute(route, href).catch((err: unknown) => console.warn("failed to open", route.path, err));
  });
}
